<template>
  <div class="room-navigation">
    <slot name="home-link" />
    <img v-if="showLogo && themeLogo" class="logo" :src="themeLogo" :alt="t('logotype')" />
    <FriedGetAcceptLogo v-else-if="showLogo" class="logo getaccept" :aria-label="t('logotype')" />
    <NavigationItem
      v-for="item in items"
      :key="item.key"
      :active="activeView === item.key"
      :item="item"
    >
      <template v-if="hasNotification(item.key)" #notification>
        <div class="notification-circle" />
      </template>
    </NavigationItem>
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import type { IconName } from '@getaccept/fried-tofu';
import { FriedGetAcceptLogo } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import { BrandingHelper } from '@getaccept/lib-shared/src/signing-theme/helpers/branding.helper';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { Conversation } from '../chat';
import { ChatHelper } from '../chat';
import { DrView } from '../types';
import type { DealroomFeatureSettings } from '../types/dsr';
import NavigationItem from './NavigationItem.vue';

export default defineComponent({
  name: 'RoomNavigation',
  components: {
    NavigationItem,
    FriedGetAcceptLogo,
  },
  props: {
    activeView: {
      required: true,
      type: String as PropType<DrView>,
    },
    logoUrl: {
      default: '',
      type: String,
    },
    userId: {
      type: String,
      default: '',
    },
    participantId: {
      type: String,
      default: '',
    },
    conversations: {
      type: Array as PropType<Conversation[]>,
      default: () => [],
    },
    isTemplate: {
      type: Boolean,
    },
    isRoomDraft: {
      type: Boolean,
    },
    showLogo: {
      type: Boolean,
      default: true,
    },
    hasUnpublishedChanges: {
      type: Boolean,
    },
    actionPlanVisible: {
      type: Boolean,
    },
    publishedView: {
      type: Boolean,
    },
    featureSettings: {
      type: Object as PropType<DealroomFeatureSettings>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();

    const themeLogo = computed(() =>
      BrandingHelper.getLogoUrl({ logoUrl: props.logoUrl } as SigningTheme)
    );

    const hasUnreadMessages = computed(() => {
      const unreadMessages = props.conversations.filter(conversation =>
        ChatHelper.isLastMessageUnread(conversation, props.userId, props.participantId)
      );
      return unreadMessages.length > 0;
    });

    const hasNotification = (key: DrView) => {
      if (key === DrView.Chat) {
        return hasUnreadMessages.value;
      } else if (key === DrView.Content) {
        return props.hasUnpublishedChanges;
      }
      return false;
    };

    const getActionPlanTooltip = computed(() => {
      if (props.actionPlanVisible) {
        return '';
      }

      return props.isTemplate
        ? t('map_tab_action_plan_not_visible_in_template_tooltip')
        : t('map_tab_action_plan_not_visible_tooltip');
    });

    const items = computed(() =>
      [
        {
          label: t('dsr_navigation_home_label'),
          key: DrView.Content,
          hidden: false,
        },
        {
          label: t('MAP_tab_action_plan'),
          key: DrView.ActionPlan,
          hidden:
            (props.featureSettings as DealroomFeatureSettings).disableActionPlan ||
            (!props.actionPlanVisible && props.publishedView),
          icon:
            props.actionPlanVisible || !!props.publishedView
              ? undefined
              : ('view-hide' as IconName),
          tooltip: getActionPlanTooltip.value,
        },
        {
          label: t('frame_files'),
          key: DrView.Files,
          hidden:
            (props.featureSettings as DealroomFeatureSettings)?.disableFiles === false
              ? false
              : true,
        },
      ].filter(navigationItem => !navigationItem.hidden)
    );

    return {
      items,
      getActionPlanTooltip,
      themeLogo,
      t,
      DrView,
      hasUnreadMessages,
      hasNotification,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '../scss/dsr-variables';

.room-navigation {
  display: flex;
  align-items: center;
  height: $room-navigation-height;

  @media screen and (max-width: $sm) {
    width: 100%;
    height: fit-content;
    padding: var(--spacing-50) var(--spacing-100);
    overflow-x: auto;
    overflow-y: hidden;
  }

  .logo {
    max-width: 10rem;
    max-height: $room-navigation-height;
    object-fit: contain;
    align-self: center;
    margin-right: var(--spacing-100);

    &.getaccept {
      height: $room-navigation-height;
    }
  }

  .notification-circle {
    width: 0.5rem;
    height: 0.5rem;
    background-color: var(--blue-45);
    border-radius: 50%;
    position: absolute;
    top: var(--spacing-25);
    right: var(--spacing-25);
    animation: soft-blink 2s ease-in-out;
    animation-iteration-count: 3;

    @media screen and (max-width: $xs) {
      top: var(--spacing-100);
      right: var(--spacing-100);
    }
  }

  @keyframes soft-blink {
    0% {
      outline: 0 solid var(--blue-93);
    }

    50% {
      outline: 4px solid var(--blue-93);
    }

    100% {
      outline: 0 solid var(--blue-93);
    }
  }
}
</style>
